// Select.jsx
import React, {
  useCallback,
  useMemo,
  useState,
  useEffect,
  useRef,
} from 'react';
import { createPortal } from 'react-dom';
import styles from './Select.module.scss';
import Label from '../../label/Label';
import Loader from 'component/atom/loader/Loader';
import { useBrandingContext } from 'app-context/BrandingsContext';
import SvgWithBranding from 'component/hoc/branding/withBrandingColor.hoc';

const Select = ({
  // api,
  label,
  placeholder,
  name,
  value: initialValue,
  onChange,
  style,
  type,
  error,
  touched,
  // skip,
  disabled,
  isLoading,
  classname,
  selectClassname,
  options,
  onSelectLabel,
  isClear = true,
  additionalIcon,
  variant,
  optionClassname,
  optionsClassName,
  inputClassName,
  onClick = () => {},
}) => {
  // const skipApiCall = !api || skip || options;
  const { brandingData } = useBrandingContext();
  const { page_TextColor } = brandingData;

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownStyle, setDropdownStyle] = useState({});

  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const wrapperRef = useRef(null); // NEW: treat wrapper as "inside"

  const sourceOptions = options;

  const handleSelect = useCallback(
    (newValue) => {
      setIsOpen(false);
      setSearchTerm('');
      const selectedLabel = sourceOptions.find(
        (option) => option.value === newValue
      )?.label;

      if (onChange) {
        onChange({
          target: {
            name,
            value: newValue,
          },
        });
      }

      if (onSelectLabel && selectedLabel) {
        onSelectLabel(selectedLabel);
      }
    },
    [name, onChange, sourceOptions, onSelectLabel]
  );

  const filteredOptions = useMemo(
    () =>
      (sourceOptions || [])
        .filter((option) =>
          option?.label?.toLowerCase().includes(searchTerm.toLowerCase())
        )
        .map((option) => ({
          value: option.value,
          label: option.label,
        })),
    [sourceOptions, searchTerm]
  );

  const selectedOption = useMemo(
    () => filteredOptions.find((option) => option.value === initialValue),
    [filteredOptions, initialValue]
  );

  // Position dropdown (above if not enough space below)
  useEffect(() => {
    if (isOpen && containerRef.current) {
      const { width, height, top, left } =
        containerRef.current.getBoundingClientRect();
      const dropdownHeight = dropdownRef.current?.offsetHeight || 200;
      const spaceBelow = window.innerHeight - (top + height);
      const spaceAbove = top;
      setDropdownStyle({
        width: `${width}px`,
        top:
          spaceBelow < dropdownHeight && spaceAbove > dropdownHeight
            ? `${top - dropdownHeight + window.scrollY}px`
            : `${top + height + window.scrollY}px`,
        left: `${left + window.scrollX}px`,
        color: page_TextColor,
      });
    }
  }, [isOpen, page_TextColor]);

  // Outside click / scroll / resize
  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedInsideDropdown =
        dropdownRef.current && dropdownRef.current.contains(event.target);
      const clickedInsideInput =
        inputRef.current && inputRef.current.contains(event.target);
      const clickedInsideWrapper =
        wrapperRef.current && wrapperRef.current.contains(event.target);

      if (
        !clickedInsideDropdown &&
        !clickedInsideInput &&
        !clickedInsideWrapper
      ) {
        setIsOpen(false);
      }
    };

    const handleScrollOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleResize = () => setIsOpen(false);

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScrollOutside, true);
      window.addEventListener('resize', handleResize);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScrollOutside, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen]);

  return (
    <div
      className={`${styles['select-container']} ${
        !label ? styles['noLabel'] : ''
      } ${classname ?? ''}`}
      style={style}
      ref={containerRef}
    >
      <div className={styles.labelCont}>
        {label && <Label text={label} htmlFor={`select-${name}`} />}
        {typeof additionalIcon === 'function'
          ? additionalIcon()
          : additionalIcon}
      </div>

      <div
        className={`${styles['select-wrapper']} ${
          disabled ? styles.disabled : ''
        } ${error && touched ? styles['input-error'] : ''}${
          selectClassname ?? ''
        }`}
        tabIndex={0}
        aria-controls={`select-dropdown-${name}`}
        aria-expanded={isOpen}
        ref={wrapperRef}
        style={disabled ? { pointerEvents: 'auto' } : undefined}
        onClick={(e) => {
          onClick?.(e); // 👈 always fire
        }}
        // Toggle on mousedown so it occurs before document's mousedown
        onMouseDown={(e) => {
          if (disabled) return;
          // If the user moused down on the input itself, don't toggle here.
          if (inputRef.current && inputRef.current.contains(e.target)) return;
          setIsOpen((prev) => !prev);
        }}
      >
        <div className={styles['select-display']}>
          {type && <img className={styles.svg} src={type} alt={placeholder} />}
          <input
            id={`select-${name}`}
            type='text'
            ref={inputRef}
            value={
              isOpen
                ? searchTerm
                : selectedOption
                ? selectedOption.label
                : placeholder
            }
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={placeholder}
            className={`${styles['select']} ${inputClassName} ${
              !selectedOption && !searchTerm
                ? styles['placeholder']
                : styles['has-value']
            }`}
            readOnly={disabled || !isOpen}
            style={{
              color:
                !selectedOption && !searchTerm ? '#757575' : page_TextColor,
            }}
            onClick={(e) => {
              onClick?.(e);
            }}
            // Prevent wrapper toggle & outside close when interacting with input
            onMouseDown={(e) => {
              if (disabled) {
                e.stopPropagation();
                return;
              }
              e.stopPropagation();
              if (!isOpen) setIsOpen(true);
            }}
            autoComplete='off'
          />
          {isLoading ? (
            <div
              className={styles.loader}
              onClick={(e) => {
                onClick?.(e);
              }}
            >
              <Loader style={{ width: '15px', height: '15px' }} isComponent />
            </div>
          ) : (
            <SvgWithBranding
              type='select'
              className={styles.icon}
              onClick={(e) => {
                onClick?.(e);
              }}
            />
          )}
        </div>

        {isOpen &&
          createPortal(
            <div
              className={`${styles['options']} ${
                styles['dropdown-animation']
              } ${optionsClassName ?? ''} ${variant ?? ''}`}
              style={dropdownStyle}
              ref={dropdownRef}
              // Keep interactions inside without triggering outside close
              onScroll={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              {filteredOptions.length > 0 ? (
                filteredOptions?.map((option, index) => (
                  <div
                    key={index}
                    className={`${styles['option']} ${
                      option.value === initialValue ? styles['selected'] : ''
                    } ${optionClassname ?? ''}`}
                    onClick={() => handleSelect(option.value)}
                  >
                    {option.label}
                  </div>
                ))
              ) : (
                <div className={styles['no-data']}>No Data</div>
              )}
            </div>,
            document.body
          )}
      </div>
      {error && touched && <div className='error-message'>{error}</div>}
    </div>
  );
};

export default Select;
